import { SideNav, BottomTabs, type NavEntry, type NavKey } from './nav';
import { TopBar } from './top-bar';
import { PhoneMenu } from './phone-menu';

/**
 * The application frame: a 200px rail on desktop, a top bar and tab bar on
 * phone, one scrolling content column in both.
 *
 * This is the piece that stops the app reading as a phone app in a desktop
 * window. `ion-tabs` had no desktop form at all — `ion-tab-bar slot="bottom"`
 * is a phone tab bar at every width — so the layout could not be reached
 * through tokens, only by owning the frame.
 *
 * Both navigations drive the same `active` key, so a section is one concept
 * with two chromes rather than two parallel trees.
 */
export const AppShell: React.FC<{
  entries: NavEntry[];
  active: NavKey;
  onSelect: (key: NavKey) => void;
  /** Sidebar identity chip — the coach, not the client. */
  identity?: React.ReactNode;
  /** Pinned to the foot of the rail. */
  navFooter?: React.ReactNode;
  onHome?: () => void;
  phoneTitle?: string;
  phoneBack?: () => void;
  phoneRight?: React.ReactNode;
  /**
   * Sections reachable on phone only through the top bar's ⋯ menu — the
   * desktop-only ones that have no other phone surface, such as Settings.
   */
  phoneMenu?: NavEntry[];
  /** Suppresses the tab bar where a page owns the whole phone viewport. */
  hidePhoneTabs?: boolean;
  children: React.ReactNode;
}> = ({
  entries,
  active,
  onSelect,
  identity,
  navFooter,
  onHome,
  phoneTitle,
  phoneBack,
  phoneRight,
  phoneMenu,
  hidePhoneTabs,
  children,
}) => (
  <div className="flex min-h-[100dvh] bg-shell font-sans text-foreground">
    <SideNav
      className="hidden lg:flex"
      entries={entries}
      active={active}
      onSelect={onSelect}
      identity={identity}
      footer={navFooter}
      onHome={onHome}
    />
    <div className="flex min-w-0 flex-1 flex-col">
      <TopBar
        className="lg:hidden"
        title={phoneTitle}
        onBack={phoneBack}
        onHome={onHome}
        right={
          phoneMenu?.length ? (
            <>
              {phoneRight}
              <PhoneMenu entries={phoneMenu} active={active} onSelect={onSelect} />
            </>
          ) : (
            phoneRight
          )
        }
      />
      <main className="min-w-0 flex-1">{children}</main>
      {!hidePhoneTabs && (
        <BottomTabs
          className="lg:hidden"
          entries={entries}
          active={active}
          onSelect={onSelect}
        />
      )}
    </div>
  </div>
);
